import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { put } from "../../lib/api";
import { useSession } from "../../lib/session";
import type { AppCatalog, AppDetail, AppPermission } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { SectionTitle } from "../ui/Text";

/**
 * What an app may read.
 *
 * Granting is held to the same rule as giving a role a permission: nobody hands
 * an outsider data they cannot see themselves. A box the operator could not tick
 * is disabled with the reason beside it, not hidden — and one already ticked by
 * someone else can still be cleared, since taking access away is always allowed.
 */

export interface AppGrantsProps {
  readonly app: AppDetail;
  readonly catalog: AppCatalog;
  readonly canEdit: boolean;
  readonly onSaved: (message: string) => void;
}

export function AppGrants({ app, catalog, canEdit, onSaved }: AppGrantsProps) {
  const { can } = useSession();
  const [draft, setDraft] = useState<readonly AppPermission[]>(app.permissions);

  const save = useMutation({
    mutationFn: () => put<AppDetail>(`/apps/${app.id}/permissions`, { permissions: draft }),
    onSuccess: () => onSaved("Permissions saved. They apply to the app's next request."),
  });

  const isDirty =
    draft.length !== app.permissions.length || draft.some((key) => !app.permissions.includes(key));

  function toggle(key: AppPermission) {
    setDraft((current) =>
      current.includes(key) ? current.filter((held) => held !== key) : [...current, key],
    );
  }

  return (
    <section aria-labelledby="app-grants" className="flex flex-col gap-4">
      <SectionTitle>
        <span id="app-grants">May read</span>
      </SectionTitle>

      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0" disabled={!canEdit}>
        <legend className="sr-only">Permissions</legend>
        {catalog.permissions.map((entry) => {
          const checked = draft.includes(entry.key);
          const locked = !checked && !can(entry.grantedBy);
          return (
            <label key={entry.key} className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={checked}
                disabled={locked}
                onChange={() => toggle(entry.key)}
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-data font-semibold text-ink">
                  {entry.label}{" "}
                  <span className="font-mono font-normal text-ink-faint">{entry.key}</span>
                </span>
                <span className="text-micro tracking-normal normal-case text-ink-muted">
                  {entry.description}
                  {locked ? ` Needs ${entry.grantedBy} to grant.` : ""}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        A grant covers everything the app acts for: every holder, or one group and the groups
        inside it. Blocked cards and VINs, and their sessions, are never shown.
      </p>

      {canEdit ? (
        <div className="flex gap-3">
          <Button
            variant="primary"
            disabled={!isDirty}
            pending={save.isPending}
            onClick={() => save.mutate()}
          >
            Save permissions
          </Button>
          {isDirty ? (
            <Button variant="ghost" onClick={() => setDraft(app.permissions)}>
              Undo
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
